<template>
  <div>
    <!-- Since the categories must be fetched first, the breadcrumbs can not be resolved on the server side -->
    <ClientOnly>
      <Container use-full-width>
        <Breadcrumbs with-current-page :breadcrumbs="breadcrumbs" />
      </Container>
    </ClientOnly>

    <ProductContent :product="product" />

    <LoaderComponents
      v-if="templateEntry && templateEntry.loader_components"
      v-editor-target="'main_components'"
      :components="templateEntry.main_components"
    />
  </div>
</template>

<script lang="ts" setup>
import { getCategoryRoute, getTranslatedProperty } from '@shopware/helpers'

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
})

const metaLinks = useProductMetaLinks(props.product)
const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const appUrl = runtimeConfig.public.APP_URL
// Define the head for the product detail page
useHead({
  title: getTranslatedProperty(props.product, 'name'),
  meta: [
    {
      name: 'description',
      content: getTranslatedProperty(props.product, 'metaDescription'),
    },
    {
      name: 'keywords',
      content: getTranslatedProperty(props.product, 'keywords') || `${getTranslatedProperty(props.product, 'name')}, cleaning products`,
    },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1',
    },
    {
      property: 'og:title',
      content: getTranslatedProperty(props.product, 'name'),
    },
    {
      property: 'og:description',
      content: getTranslatedProperty(props.product, 'metaDescription'),
    },
    {
      property: 'og:image',
      content: props.product.cover?.media?.url,
    },
    {
      property: 'og:url',
      content: `${appUrl}${route.path}`,
    },
    {
      property: 'og:type',
      content: 'product',
    },
    // Twitter Card tags
    {
      name: 'twitter:card',
      content: 'summary_large_image', // Or 'summary' for smaller image
    },
    {
      name: 'twitter:title',
      content: getTranslatedProperty(props.product, 'name'),
    },
    {
      name: 'twitter:description',
      content: getTranslatedProperty(props.product, 'metaDescription'),
    },
    {
      name: 'twitter:image',
      content: props.product.cover?.media?.url,
    },
  ],
  link: metaLinks,
})

// Fetch the product template page from statamic
const { locale } = useI18n()
const templateEntry = computed(() => {
  return data.value?.entry
})

// Fetch the product template page from statamic
const slug = 'produkt-detail-vorlage'
const { data, error } = await useAsyncGql('ProductDetailTemplate', {
  slug: slug,
  site: locale.value,
  status: '(draft|expired|scheduled|published)', // Fetch all status
})

if (error.value) {
  useGqlError((err) => {
    // Only log during development
    if (process.env.NODE_ENV === 'production') return

    // throw graphql errors when they exist
    if (err.gqlErrors && err.gqlErrors.length) {
      throw createError({
        statusCode: 500,
        statusMessage: 'GraphQL Error',
        message: err.gqlErrors[0].message,
        data: err.gqlErrors[0],
      })
    }

    class FetchError extends Error {
      constructor(
        message: string,
        public status: number,
        public cause?: Error,
      ) {
        super(message)
      }
    }

    throw new FetchError('Could not fetch template page (initial)', 500, error.value)
  })
}

if (!data.value || !data.value.entry) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Entry not found',
    message: 'No entry found as template for the product detail pages',
  })
}

// Fetch the categories from the context
const categories = inject<Schemas['Category'][] | null>('categories', null)

const localePath = useLocalePath()
const breadcrumbs = computed(() => {
  const { categoryTree } = props.product

  // Get the categories from the categoryTree and filter out empty categories as if they are not in the provided categories array, they are not relevant for the breadcrumbs
  const productCategories = categoryTree.map((categoryId) => {
    return categories?.value?.find(category => category.id === categoryId) || null
  }).filter(Boolean)

  const productBreadcrumbs = productCategories.map((category) => {
    const categoryRoute = getCategoryRoute(category)
    const cleanedPath = (typeof categoryRoute === 'object' && 'path' in categoryRoute)
      ? categoryRoute.path.split('/').filter(Boolean)
      : []

    return {
      id: category.id,
      slug: category.id,
      title: getTranslatedProperty(category, 'name'),
      url: localePath({
        name: 'products-all',
        params: { all: cleanedPath },
      }),
      entry: {
        published: true,
      },
    }
  })

  // Add homepage to the breadcrumbs
  productBreadcrumbs.unshift({
    id: 'home',
    slug: 'home',
    title: 'Home',
    url: localePath('/'),
    entry: {
      published: true,
    },
  })

  return productBreadcrumbs || []
})

onMounted(() => {
  // console.log('Product', props.product)
})
</script>
